/**
 * Sortie d'un exercice après un paiement (Stripe recharge l'app).
 *
 * Au retour de Stripe, l'historique du navigateur contient la page de paiement
 * et la page « C'est débloqué » : un simple `history.back()` y renvoie. Avant de
 * partir payer, on mémorise donc la page qui avait ouvert l'exercice (page
 * SKILLCARD, chapitre, tableau de bord…) ; le bouton retour de l'exercice y
 * ramène directement (`location.replace`), une seule fois.
 */

const PREFIX = 'exercise-exit-';
/** Au-delà, la mémoire est périmée (paiement abandonné, etc.). */
export const EXERCISE_EXIT_TTL_MS = 2 * 60 * 60 * 1000;

let previousPath: string | null = null;

/** Mémorise la page précédente à chaque navigation SPA (branché dans main). */
export function notePathChange(from: string | null | undefined, to: string): void {
  if (from && from !== to) previousPath = from;
}

export function getPreviousPath(): string | null {
  return previousPath;
}

/** Chemin interne sûr (pas d'hôte, pas de `//`) — anti open redirect. */
function safePath(path: unknown): string | null {
  if (typeof path !== 'string' || path.length > 500) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;
  return path;
}

/** Une page de paiement ou l'exercice lui-même ne sont jamais une destination de sortie. */
function isUsableExit(path: string, exerciseId: string): boolean {
  return !path.includes(exerciseId) && !/\/acheter(\/|\?|$)/.test(path);
}

/**
 * Avant de partir payer : retient la page qui a ouvert l'exercice, sinon
 * `fallback` (ex. la page de la SKILLCARD).
 */
export function rememberExerciseExit(
  exerciseId: string,
  fallback: string | null,
  now = Date.now(),
): void {
  const prev = safePath(previousPath);
  const path = prev && isUsableExit(prev, exerciseId) ? prev : safePath(fallback);
  try {
    if (path) sessionStorage.setItem(PREFIX + exerciseId, JSON.stringify({ path, at: now }));
    else sessionStorage.removeItem(PREFIX + exerciseId);
  } catch {
    /* stockage indisponible : retour classique */
  }
}

/** Lit ET efface la sortie mémorisée (null si absente ou périmée). */
export function consumeExerciseExit(exerciseId: string, now = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(PREFIX + exerciseId);
    if (!raw) return null;
    sessionStorage.removeItem(PREFIX + exerciseId);
    const parsed = JSON.parse(raw) as { path?: unknown; at?: unknown };
    if (typeof parsed.at !== 'number' || now - parsed.at > EXERCISE_EXIT_TTL_MS) return null;
    return safePath(parsed.path);
  } catch {
    return null;
  }
}
